import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, requireImageUrl, PHOTO_GRADE } from "./types";

// venueTrend — a trend pin ("Wedding Trends 2027"). A full-bleed graded photo
// fills the whole pin; a centered semi-transparent white card floats over it
// with a thin single-line frame inset around its perimeter. The headline and a
// bulleted list of trends sit inside the card. Covers the "Trend Pin" type.
//   imageUrl  → full-bleed background photo
//   venue.name / title → the headline
//   items[]   → up to 6 { name (trend) }
const INK = "#2b2520";
const GOLD = "#c39a4f";
const FRAME = "rgba(195,154,79,.6)"; // thin gold perimeter line

export function venueTrendTemplate(input: PinInput): string {
  const { brand, eyebrow, typography, venue, items } = input;
  const imageUrl = requireImageUrl(input, "venueTrend");
  const title = (venue?.name || input.title || "").trim();
  const bullets = (items ?? []).filter((i) => i.name).slice(0, 6);
  if (!bullets.length) throw new Error("venueTrend template requires items[] with names");

  const tLen = title.length;
  const titleSize = tLen <= 20 ? 72 : tLen <= 30 ? 60 : 50;
  const n = bullets.length;
  const bFont = n <= 4 ? 36 : n <= 5 ? 33 : 30;
  const bPad = n <= 4 ? 13 : n <= 5 ? 11 : 9;

  const rows = bullets
    .map((b) => `<div class="b" style="padding:${bPad}px 0"><span class="dot">•</span><span class="txt">${escapeHtml(b.name)}</span></div>`)
    .join("");

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:#000;color:${INK}}
  .pin{position:relative;width:100%;height:100%;overflow:hidden}
  /* Full-bleed graded photo behind everything. */
  .photo{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:${Math.round((input.focusX ?? 0.5) * 100)}% ${Math.round((input.focusY ?? 0.5) * 100)}%;filter:${PHOTO_GRADE}}
  /* Soft overall darken so the white card edge reads against any photo. */
  .photo::after{content:"";position:absolute;inset:0;background:rgba(0,0,0,.18)}
  /* Centered semi-transparent white card — 75% of the pin's framed area. */
  .card{position:absolute;top:233px;bottom:233px;left:170px;right:170px;background:rgba(255,255,255,.86);z-index:2;display:flex;flex-direction:column;padding:56px 48px 46px;box-shadow:0 18px 60px rgba(0,0,0,.28)}
  /* Thin single-line frame inset around the card perimeter. */
  .card::before{content:"";position:absolute;inset:16px;border:1.5px solid ${FRAME};pointer-events:none}
  .head{text-align:center;flex-shrink:0}
  ${eyebrow ? `.eyebrow{font-size:22px;letter-spacing:5px;text-transform:uppercase;font-weight:700;color:${GOLD};margin-bottom:14px}` : ""}
  .title{font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};font-size:${titleSize}px;line-height:1.06;letter-spacing:-1px;color:${INK};padding-bottom:.06em;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .tdiv{width:140px;height:1px;background:${FRAME};margin:16px auto 0}
  .list{display:flex;flex-direction:column;flex:1;justify-content:center}
  .b{display:flex;align-items:baseline;gap:18px}
  .dot{color:${GOLD};font-size:${bFont + 8}px;line-height:1;flex-shrink:0}
  .txt{font-size:${bFont}px;line-height:1.25;font-weight:600;color:${INK}}
  .brand{text-align:center;font-size:18px;letter-spacing:4px;text-transform:uppercase;font-weight:700;color:${GOLD};flex-shrink:0}
</style></head>
<body><div class="pin">
  <div class="photo"></div>
  <div class="card">
    <div class="head">
      ${eyebrow ? `<div class="eyebrow">${escapeHtml(eyebrow)}</div>` : ""}
      <div class="title">${escapeHtml(title)}</div>
      <div class="tdiv"></div>
    </div>
    <div class="list">${rows}</div>
    ${brand ? `<div class="brand">${escapeHtml(brand)}</div>` : ""}
  </div>
</div></body></html>`;
}
